import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { LocalDate } from '../../../core/model/local-date';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { AccountingPeriodCloseResult, AccountingPeriodReopenInfo, OpeningBalanceRun } from '../../model/opening-balance-run';

@Injectable()
export class AccountingPeriodService {
  private readonly endpoint: string;

  constructor(
    private readonly client: HttpClient,
    private readonly notification: MessageService,
    private readonly progressBar: ProgressBarService,
    @Inject(APP_CONFIG) config: IAppConfig
  ) {
    this.endpoint = `${config.apiEndpointV2}/accounting-period`;
  }

  close(companyId: string, closeDate: LocalDate, description: string): Observable<OpeningBalanceRun> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/close`, { companyId, closeDate, description }).pipe(
      map(data => new OpeningBalanceRun(data)),
      tap(() => this.notification.showSuccess('ACCOUNTING_PERIOD.CLOSE_SUCCESS')),
      catchError(error => {
        this.notification.showError(error);
        return of(null);
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  closeMany(companyIds: string[], closeDate: LocalDate, description: string): Observable<AccountingPeriodCloseResult> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/close-many`, { companyIds, closeDate, description }).pipe(
      map(data => new AccountingPeriodCloseResult(data)),
      tap(() => this.notification.showSuccess('ACCOUNTING_PERIOD.CLOSE_MANY_SUCCESS')),
      catchError(error => {
        this.notification.showError(error);
        return of(null);
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  reopen(companyId: string, reopenToDate: LocalDate, reason: string, confirmed: boolean): Observable<OpeningBalanceRun> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/reopen`, { companyId, reopenToDate, reason, confirmed }).pipe(
      map(data => new OpeningBalanceRun(data)),
      tap(() => this.notification.showSuccess('ACCOUNTING_PERIOD.REOPEN_SUCCESS')),
      catchError(error => {
        this.notification.showError(error);
        return of(null);
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getReopenInfo(companyId: string): Observable<AccountingPeriodReopenInfo> {
    return this.client.get<any>(`${this.endpoint}/reopen-info/${companyId}`).pipe(
      map(data => new AccountingPeriodReopenInfo(data)),
      catchError(error => {
        this.notification.showError(error);
        return of(null);
      })
    );
  }
}
